"use client"

import type React from "react"
import {useEffect, useRef, useState} from "react"
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from "@/components/ui/dialog"
import {Button} from "@/components/ui/button"
import ServiceComment from "@/http/api/profile/comments/CommentServices"
import {type SubmitHandler, useForm} from "react-hook-form"
import FileReaders from "@/hook/FileReaders"
import {SingleProductEnum} from "@/constants/commerce-enum"
import type {IAddCommentFormDataProps} from "@/interface/components/profile/comment"
import {useAuthStore} from "@/store/auth/authStore"
import WizardFormAuth from "@/components/global/modals/wizard-auth-form-modal"
import {useMobile} from "@/hook/useMobile"
import {AddNewCommentMobile} from "@/components/ui/add-new-comment/add-new-comment-mobile";
import {AddNewCommentDesktop} from "@/components/ui/add-new-comment/add-new-comment-desktop";
import {commentRates} from "./comment-rates-data"

// --------------------------------------------------------------------------------------------------------------------->

interface CommentRate {
    id: number
    title: string
    value: number
    color: string
    icon: any
}


function AddNewComment({
                           id,
                           title,
                           isOpen,
                           onOpenChange,
                           onCloseFirstModal,
                       }: {
    id: number
    title: string
    isOpen?: any
    onOpenChange?: any
    onCloseFirstModal?: any
}) {
    const {token} = useAuthStore((state) => state)
    const isMobile = useMobile()

    const [selectedOption, setSelectedOption] = useState<CommentRate>(commentRates[0])
    const inputFileRef = useRef<HTMLInputElement>(null)
    const [viewImage, setViewImage] = useState<string[]>([])

    const handleClick = () => {
        inputFileRef.current?.click()
    }

    const {createComment} = new ServiceComment()

    const {register, handleSubmit, watch, getValues, setValue} = useForm()

    const deleteHandler = (indexImage: number) => {
        setViewImage((prevImages: any) => prevImages.filter((_: unknown, index: number) => index !== indexImage))
        setValue(
            "images",
            getValues()?.images.filter((_: unknown, index: number) => index !== indexImage),
        )
    }

    const imageHandler = () => {
        if (watch()?.images?.length > 0) {
            getValues().images.map((item: File) => {
                FileReaders(item, setViewImage)
            })
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : []
        setValue("images", files)
        imageHandler()
    }

    const onSubmit: SubmitHandler<any> = async (value: IAddCommentFormDataProps) => {
        const formData = new FormData()
        for (let i = 0; i < value?.images?.length; i++) {
            formData.append(`comment_image[${i}]`, value?.images[i])
        }
        formData.append("is_private", String(value?.is_private ? 1 : 0))
        formData.append("comment_rating", String(selectedOption.value))
        formData.append("product_id", String(id))
        formData.append("content", value?.content)

        try {
            await createComment(formData)
        } catch (error) {
            console.error("Error submitting comment:", error)
        } finally {
            onOpenChange(false)
        }
    }

    useEffect(() => {
        setSelectedOption(commentRates[0])
    }, [])

    if (!token) {
        return (
            <WizardFormAuth
                customButton={
                    <Button variant={"outline"} className={"w-full md:h-14"}>
                        ثبت نظر
                    </Button>
                }
            />
        )
    }

    // Common props for both desktop and mobile components
    const commentProps = {
        id,
        title,
        isOpen,
        onOpenChange,
        onCloseFirstModal,
        selectedOption,
        setSelectedOption,
        inputFileRef,
        viewImage,
        setViewImage,
        handleClick,
        register,
        handleSubmit,
        watch,
        getValues,
        setValue,
        deleteHandler,
        handleFileChange,
        onSubmit,
        commentRates,
    }

    // Add new comment on mobile
    if (isMobile) {
        return <AddNewCommentMobile {...commentProps} />
    }

    return (

        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild className="w-full">
                <div className={"flex justify-end"}>
                    <Button
                        variant={"outline"}
                        className={"w-full md:h-14"}
                        onClick={() => {
                            onCloseFirstModal()
                            onOpenChange(true)
                        }}
                    >
                        ثبت نظر
                    </Button>
                </div>
            </DialogTrigger>
            <DialogContent className="w-11/12 md:max-w-[800px] rounded-2xl overflow-y-scroll h-5/6 md:h-auto md:pb-2">
                <DialogHeader>
                    <DialogTitle
                        className="w-full flex flex-wrap items-center justify-start text-sm md:text-base md:ps-5">
                        <div className="w-full flex text-base flex-wrap text-blue-secondary md:py-2">
                            {SingleProductEnum.yourComment}
                        </div>
                        <div
                            className="text-start w-full flex text-xs flex-wrap text-gray-400 pt-2 md:py-2">{title}</div>
                    </DialogTitle>
                    <AddNewCommentDesktop register={register} commentRates={commentRates}
                                          setSelectedOption={setSelectedOption} selectedOption={selectedOption}
                                          viewImage={viewImage}
                                          inputFileRef={inputFileRef} handleFileChange={handleFileChange}
                                          handleClick={handleClick} deleteHandler={deleteHandler}
                                          setValue={setValue} handleSubmit={handleSubmit} onSubmit={onSubmit}/>
                </DialogHeader>
            </DialogContent>
        </Dialog>
    )
}

export default AddNewComment

